<!-- For an unknown reason the generic here is necessary, otherwise the typings of the component break -->
<script setup lang="ts" generic="_">
import { createMenuButton } from "@sit-onyx/headless";
import { iconMoreVertical } from "@sit-onyx/icons";
import { computed, provide, ref, watch, type ComponentInstance, type VNodeRef } from "vue";
import { useResizeObserver } from "../../../../composables/useResizeObserver.js";
import { useVModel } from "../../../../composables/useVModel.js";
import { injectI18n } from "../../../../i18n/index.js";
import { mergeVueProps } from "../../../../utils/attrs.js";
import OnyxBasicPopover from "../../../OnyxBasicPopover/OnyxBasicPopover.vue";
import OnyxSystemButton from "../../../OnyxSystemButton/OnyxSystemButton.vue";
import {
  MENU_ITEM_DRILLDOWN_INJECTION_KEY,
  type NestedMenuDrilldownModeContext,
} from "../OnyxMenuItem/types.js";
import type { OnyxFlyoutMenuProps } from "./types.js";

const props = withDefaults(defineProps<OnyxFlyoutMenuProps>(), {
  trigger: "hover",
  open: undefined,
  alignment: "auto",
  drilldownMode: "internal",
});

const emit = defineEmits<{
  /**
   * Emitted when the isExpanded state changes.
   */
  "update:open": [value: boolean];
}>();

/**
 * If the flyout is expanded or not.
 */
const isExpanded = useVModel({
  props,
  emit,
  key: "open",
  default: false,
});

const slots = defineSlots<{
  /**
   * The trigger for the flyout menu. Use with `OnyxButton`, `OnyxSystemButton` or `OnyxIconButton`
   */
  button?(params: {
    /**
     * Attributes and event listeners that must be bound to an interactive element (button or link),
     * that should act as the flyout trigger.
     */
    trigger: object;
  }): unknown;
  /**
   * Use this slot to provide `OnyxMenuItem` components.
   */
  options?(): unknown;
  /**
   * Optional header content to display above the options.
   */
  header?(): unknown;
  /**
   * Optional footer content to display below the options.
   */
  footer?(): unknown;
}>();

const popover = ref<ComponentInstance<typeof OnyxBasicPopover>>();
const menuRef = ref<HTMLElement>();
const setMenuRef: VNodeRef = (el) => (menuRef.value = el as typeof menuRef.value);

const actualPosition = computed(() => popover.value?.popoverPosition);

const { t } = injectI18n();

const {
  elements: { root, button, menu },
} = createMenuButton({
  isExpanded: computed(() => !!isExpanded.value),
  onToggle: () => (isExpanded.value = !isExpanded.value),
  trigger: computed(() => props.trigger),
  disabled: computed(() => props.disabled),
  position: computed(() => (actualPosition.value?.includes("top") ? "top" : "bottom")),
});

const resetMinHeight = () => {
  minHeight.value = undefined;
};

// Provide the context so that all OnyxMenuItems within this flyout adapt properly to the nested mode.
provide<NestedMenuDrilldownModeContext>(MENU_ITEM_DRILLDOWN_INJECTION_KEY, {
  drilldownMode: computed(() => props.drilldownMode),
  resetMinHeight,
});

const minHeight = ref<number>();

const isObserverDisabled = computed(() => {
  return !isExpanded.value || props.drilldownMode !== "internal" || props.trigger !== "hover";
});

const { height } = useResizeObserver(menuRef, { disabled: isObserverDisabled });

watch(
  [height, isObserverDisabled],
  ([newHeight, isDisabled]) => {
    minHeight.value = isDisabled ? undefined : Math.max(minHeight.value ?? 0, newHeight);
  },
  { immediate: true },
);
</script>

<template>
  <OnyxBasicPopover
    v-bind="mergeVueProps(root, { ref: popover as VNodeRef | undefined })"
    class="onyx-component onyx-flyout-menu"
    :open="isExpanded"
    :label="props.label"
    :alignment="props.alignment"
    :position="props.position"
    :disabled="disabled"
  >
    <template v-if="slots.options || slots.header || slots.footer" #default>
      <slot name="button" :trigger="button">
        <OnyxSystemButton
          v-bind="button"
          :icon="iconMoreVertical"
          :label="t(`flyoutMenu.toggleActions.${props.trigger}`)"
        />
      </slot>
    </template>
    <!-- `v-show` instead of `v-if` is necessary, so that we can allow (teleported) dialogs to be shown -->
    <template #content>
      <!-- We always want to render the header so that we can render the padding here -->
      <div class="onyx-flyout-menu__list-header">
        <slot name="header"></slot>
      </div>

      <ul
        v-if="slots.options"
        v-bind="mergeVueProps(menu, { ref: setMenuRef })"
        class="onyx-flyout-menu__wrapper onyx-flyout-menu__group"
        :style="{
          '--onyx-flyout-menu-min-height': minHeight ? `${minHeight}px` : undefined,
        }"
      >
        <slot name="options"></slot>
      </ul>

      <!-- We always want to render the footer so that we can render the padding here -->
      <div class="onyx-flyout-menu__list-footer">
        <slot name="footer"></slot>
      </div>
    </template>
  </OnyxBasicPopover>
</template>

<style lang="scss">
@use "../../../../styles/mixins/layers";

.onyx-flyout-menu {
  @include layers.component() {
    --onyx-flyout-menu-gap: var(--onyx-spacing-2xs);
    display: inline-flex;
    width: min-content;
    position: relative;

    &__list {
      &-header {
        position: sticky;
        top: 0;
        min-height: var(--onyx-spacing-2xs);
        width: 100%;
      }

      &-footer {
        position: sticky;
        bottom: 0;
        min-height: var(--onyx-spacing-2xs);
        width: 100%;
      }
    }

    &__wrapper {
      width: 100%;
      padding: 0;
      min-height: var(--onyx-flyout-menu-min-height);
      /**
       * The last option should only be half visible:
       * 7.5 * OnyxListItem, where OnyxListItem => 2 * padding + line-height of OnyxListItem
       */
      max-height: calc(
        (var(--onyx-flyout-menu-visible-item-count, 7) + 0.5) * (2 * var(--onyx-density-xs) + 1lh)
      );
      overflow: auto;

      // when nested item is open, hide all other items in the same layer
      &:has(> .onyx-menu-item--internal.onyx-menu-item--open) {
        > .onyx-menu-item:not(.onyx-menu-item--open) {
          display: none;
        }
      }
    }
  }
}
.dark .onyx-flyout-menu__list {
  @include layers.component() {
    outline: var(--onyx-1px-in-rem) solid var(--onyx-color-component-border-neutral);
  }
}
</style>
